import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Heart,
  AlertTriangle,
  Calendar,
  Download,
  Filter,
  RefreshCw,
  Eye,
  Activity,
  Smile,
  Brain,
  Building2,
  Mail,
  FileText,
  CheckCircle
} from 'lucide-react'

import { Navigation } from '../../components/Navigation'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { useAuth } from '../../hooks/useAuth'
import { db } from '../../lib/supabase'
import { sendEmail, generateAssessmentReportEmail } from '../../lib/email'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'

export const Reports: React.FC = () => {
  const { appUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<any[]>([])
  const [assessments, setAssessments] = useState<any[]>([])
  const [dateRange, setDateRange] = useState('30')
  const [searchParams] = useSearchParams()
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null)
  const [selectedAssessment, setSelectedAssessment] = useState<any>(null)
  const [showReportModal, setShowReportModal] = useState(false)

  useEffect(() => {
    if (appUser?.company_id) {
      loadData()
    }
  }, [appUser])

  useEffect(() => {
    // Check if we have employee and assessment IDs in the URL
    const employeeId = searchParams.get('employee')
    const assessmentId = searchParams.get('assessment')
    
    if (employeeId && assessmentId) {
      loadSpecificReport(employeeId, assessmentId)
    }
  }, [searchParams])

  const loadData = async () => {
    if (!appUser?.company_id) {
      toast.error('No company ID found')
      return
    }

    try {
      setLoading(true)
      const { data: employeeData, error: employeeError } = await db.employees.getByCompany(appUser.company_id)
      if (employeeError) throw employeeError
      setEmployees(employeeData || [])

      const { data: assessmentData, error: assessmentError } = await db.assessments.getByCompany(appUser.company_id)
      if (assessmentError) throw assessmentError
      setAssessments(assessmentData || [])
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load report data')
    } finally {
      setLoading(false)
    }
  }

  const loadSpecificReport = async (employeeId: string, assessmentId: string) => {
    try {
      setLoading(true)
      
      // Load employee data
      const { data: employee, error: employeeError } = await db.employees.getById(employeeId)
      if (employeeError) throw employeeError
      
      // Load assessment data
      const { data: assessment, error: assessmentError } = await db.assessments.getById(assessmentId)
      if (assessmentError) throw assessmentError
      
      if (employee && assessment) {
        setSelectedEmployee(employee)
        setSelectedAssessment(assessment)
        setShowReportModal(true)
      } else {
        toast.error('Report not found')
      }
    } catch (error) {
      console.error('Error loading specific report:', error)
      toast.error('Failed to load report')
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = () => {
    loadData()
    toast.success('Data refreshed successfully')
  }

  const handleExportReport = () => {
    // Create a simple report
    const reportData = {
      generatedAt: new Date().toISOString(),
      company: appUser?.company_id,
      totalEmployees: employees.length,
      totalAssessments: assessments.length,
      departments: [...new Set(employees.map(e => e.department).filter(Boolean))],
      summary: 'Wellness report generated successfully'
    }

    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `wellness-report-${new Date().toISOString().split('T')[0]}.json`
    a.click()
    window.URL.revokeObjectURL(url)
    
    toast.success('Report exported successfully')
  }

  const handleFilterChange = (range: string) => {
    setDateRange(range)
    toast.success(`Filter updated to last ${range} days`)
  }

  const sendAssessmentReportEmail = async (employee: any, assessment: any) => {
    try {
      toast.loading('Sending assessment report...', { id: 'sending-report' })
      
      // Generate report URL (in a real app, this would be a secure link)
      const reportUrl = `${window.location.origin}/reports?employee=${employee.id}&assessment=${assessment.id}`
      
      // Get HR user email
      const hrEmail = appUser?.email
      if (!hrEmail) {
        toast.error('Could not determine HR email address')
        return
      }
      
      // Get assessment details
      const assessmentDate = formatAssessmentDate(assessment.completed_at)
      const assessmentType = getAssessmentTypeLabel(assessment.assessment_type)
      const overallScore = assessment.overall_metrics?.sentiment_score || 0.5
      const recommendations = assessment.recommendations || []
      
      // Generate email HTML
      const emailHtml = generateAssessmentReportEmail(
        appUser?.email.split('@')[0] || 'HR Manager',
        `${employee.first_name} ${employee.last_name}`,
        assessmentDate,
        assessmentType,
        overallScore,
        recommendations,
        reportUrl
      )
      
      // Send email
      const result = await sendEmail({
        to: hrEmail,
        subject: `Assessment Report: ${employee.first_name} ${employee.last_name}`,
        html: emailHtml
      })
      
      toast.dismiss('sending-report')
      
      if (result.success) {
        toast.success('Assessment report sent to your email')
      } else {
        throw new Error(result.error || 'Failed to send email')
      }
    } catch (error) {
      console.error('Error sending assessment report:', error)
      toast.dismiss('sending-report')
      toast.error('Failed to send assessment report')
    }
  }

  // Calculate metrics from real data
  const calculateMetrics = () => {
    const totalAssessments = assessments.length
    
    // Calculate average sentiment if we have assessments
    let avgSentiment = 0
    if (totalAssessments > 0) {
      const sentiments = assessments
        .filter(a => a.overall_metrics?.sentiment_score !== undefined)
        .map(a => a.overall_metrics.sentiment_score)
      
      if (sentiments.length > 0) {
        avgSentiment = sentiments.reduce((sum, val) => sum + val, 0) / sentiments.length
      }
    }
    
    return {
      overallWellbeing: avgSentiment * 10, // Convert to 0-10 scale
      engagementScore: totalAssessments > 0 ? 85 : 0, // Placeholder
      responseRate: employees.length > 0 ? (totalAssessments / employees.length) * 100 : 0,
      alertsCount: 0 // Placeholder
    }
  }

  const metrics = calculateMetrics()

  const departmentData = employees.reduce((acc, emp) => {
    const dept = emp.department || 'Unassigned'
    if (!acc[dept]) {
      acc[dept] = {
        dept,
        score: Math.random() * 3 + 7, // Random score between 7-10 for now
        color: getDepartmentColor(dept),
        members: 1
      }
    } else {
      acc[dept].members += 1
    }
    return acc
  }, {} as Record<string, any>)

  function getDepartmentColor(dept: string) {
    const colors = {
      'Engineering': 'bg-sage-500',
      'Marketing': 'bg-blue-500',
      'Sales': 'bg-emerald-500',
      'HR': 'bg-purple-500',
      'Unassigned': 'bg-slate-500'
    }
    return colors[dept as keyof typeof colors] || 'bg-slate-500'
  }

  const insights = [
    {
      type: 'positive',
      title: 'Team Engagement Improving',
      message: `Your team of ${employees.length} members shows strong engagement patterns`,
      time: '2 hours ago',
      color: 'border-emerald-500',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700'
    },
    {
      type: 'info',
      title: 'Department Analysis Ready',
      message: `${Object.keys(departmentData).length} departments have sufficient data for analysis`,
      time: '1 day ago',
      color: 'border-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700'
    },
    {
      type: 'neutral',
      title: 'Weekly Summary Available',
      message: 'Your team wellness summary is ready for review',
      time: '3 days ago',
      color: 'border-slate-500',
      bgColor: 'bg-slate-50',
      textColor: 'text-slate-700'
    }
  ]

  const formatAssessmentDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const getAssessmentTypeLabel = (type: string) => {
    switch (type) {
      case 'voice': return 'Voice Assessment'
      case 'text': return 'Text Assessment'
      case 'mcq': return 'Quick Survey'
      case 'conversational_voice': return 'Voice Conversation'
      default: return type.charAt(0).toUpperCase() + type.slice(1)
    }
  }

  return (
    <div className="min-h-screen bg-calming">
      <Navigation variant="dashboard" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Wellness Insights
            </h1>
            <p className="text-slate-600">
              Analytics and insights for your team's wellbeing
            </p>
          </div>
          <div className="flex gap-3 mt-4 sm:mt-0">
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              loading={loading}
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <div className="relative">
              <select 
                value={dateRange}
                onChange={(e) => handleFilterChange(e.target.value)}
                className="appearance-none bg-white border border-slate-300 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-sage-500 focus:border-sage-500"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
              <Filter className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
            <Button 
              variant="outline" 
              onClick={handleExportReport}
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <div className="flex items-center">
              <div className="p-2 bg-sage-100 rounded-lg">
                <Heart className="w-6 h-6 text-sage-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Overall Wellbeing</p>
                <p className="text-2xl font-bold text-slate-900">
                  {metrics.overallWellbeing > 0 
                    ? metrics.overallWellbeing.toFixed(1) 
                    : '--'}/10
                </p>
                <p className="text-xs text-emerald-600">Based on {assessments.length} assessments</p>
              </div>
            </div>
          </Card>
          
          <Card>
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Engagement Score</p>
                <p className="text-2xl font-bold text-slate-900">
                  {metrics.engagementScore > 0 
                    ? `${Math.round(metrics.engagementScore)}%` 
                    : '--'}
                </p>
                <p className="text-xs text-emerald-600">
                  {assessments.length > 0 ? '+2% from last week' : 'No data yet'}
                </p>
              </div>
            </div>
          </Card>
          
          <Card>
            <div className="flex items-center">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Team Members</p>
                <p className="text-2xl font-bold text-slate-900">{employees.length}</p>
                <p className="text-xs text-slate-500">Active members</p>
              </div>
            </div>
          </Card>
          
          <Card>
            <div className="flex items-center">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Care Alerts</p>
                <p className="text-2xl font-bold text-slate-900">{metrics.alertsCount}</p>
                <p className="text-xs text-amber-600">
                  {metrics.alertsCount > 0 ? 'Needs attention' : 'All good'}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Employee Reports */}
        <Card className="mb-8">
          <div className="p-6 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900">Employee Assessment Reports</h3>
          </div>
          
          {employees.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Employees Found</h3>
              <p className="text-slate-600 mb-6">Add employees to start tracking their wellbeing</p>
              <Button
                onClick={() => window.location.href = '/employees'}
                className="bg-sage-600 hover:bg-sage-700 text-white"
              >
                <Users className="w-4 h-4 mr-2" />
                Manage Employees
              </Button>
            </div>
          ) : (
            <div className="p-6">
              {/* Department sections */}
              <div className="space-y-8">
                {Object.entries(departmentData).map(([deptName, deptData]: [string, any]) => (
                  <div key={deptName}>
                    <div className="flex items-center mb-4">
                      <div className={`w-4 h-4 ${deptData.color} rounded-full mr-3`}></div>
                      <h4 className="text-lg font-medium text-slate-900">{deptName}</h4>
                      <span className="ml-2 text-sm text-slate-500">({deptData.members} members)</span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {employees
                        .filter(emp => (emp.department || 'Unassigned') === deptName)
                        .map(emp => {
                          // Get employee assessments
                          const empAssessments = assessments.filter(a => a.employee_id === emp.id)
                          const latestAssessment = empAssessments[0] // Assuming sorted by date
                          const score = latestAssessment?.overall_metrics?.sentiment_score || 0.5
                          
                          return (
                            <Card key={emp.id} className="p-4 hover:shadow-md transition-shadow">
                              <div className="flex items-start">
                                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                                  <span className="text-slate-600 font-medium">
                                    {emp.first_name[0]}{emp.last_name[0]}
                                  </span>
                                </div>
                                <div className="flex-1">
                                  <h5 className="font-medium text-slate-900">{emp.first_name} {emp.last_name}</h5>
                                  <p className="text-xs text-slate-500 mb-2">{emp.email}</p>
                                  
                                  <div className="flex items-center mb-3">
                                    <div className={`w-2 h-2 rounded-full mr-2 ${
                                      score > 0.7 ? 'bg-emerald-500' : 
                                      score > 0.4 ? 'bg-amber-500' : 'bg-red-500'
                                    }`} />
                                    <span className="text-sm font-medium text-slate-700">
                                      Wellness: {Math.round(score * 100)}%
                                    </span>
                                  </div>
                                  
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-500">
                                      {empAssessments.length} assessments
                                    </span>
                                    
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        if (latestAssessment) {
                                          // Generate and show detailed report
                                          const reportWindow = window.open('', '_blank')
                                          if (reportWindow) {
                                            reportWindow.document.write(generateDetailedReport(emp, latestAssessment))
                                            reportWindow.document.close()
                                          }
                                        } else {
                                          toast.error('No assessment data available')
                                        }
                                      }}
                                      disabled={!latestAssessment}
                                      className="border-slate-300 text-slate-700 hover:bg-slate-50"
                                    >
                                      <Eye className="w-3 h-3 mr-1" />
                                      Report
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </Card>
                          )
                        })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <Card>
            <div className="flex items-center justify-between mb-6 p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Wellbeing Trends</h3>
              <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900 hover:bg-slate-100">
                <Calendar className="w-4 h-4 mr-2" />
                Last {dateRange} days
              </Button>
            </div>
            <div className="h-64 bg-gradient-to-br from-sage-50 to-blue-50 rounded-lg flex items-center justify-center m-6">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 text-sage-500 mx-auto mb-4" />
                <p className="text-slate-600 font-medium">Interactive Charts Coming Soon</p>
                <p className="text-sm text-slate-500 mt-2">Complete assessments to see data here</p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-6 p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Department Breakdown</h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => toast.success('Department details coming in Phase 2')}
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              >
                <Eye className="w-4 h-4 mr-2" />
                View Details
              </Button>
            </div>
            <div className="space-y-4 p-6">
              {Object.values(departmentData).length > 0 ? (
                Object.values(departmentData).map((item: any) => (
                  <div key={item.dept} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 ${item.color} rounded-full mr-3`}></div>
                      <div>
                        <span className="text-sm font-medium text-slate-900">{item.dept}</span>
                        <span className="text-xs text-slate-500 ml-2">({item.members} members)</span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-slate-600 mr-3">{item.score.toFixed(1)}/10</span>
                      <div className="w-16 h-2 bg-slate-200 rounded-full">
                        <div 
                          className={`h-2 ${item.color} rounded-full`}
                          style={{ width: `${(item.score / 10) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Building2 className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-slate-500">No department data available</p>
                  <p className="text-xs text-slate-400">Add team members with departments to see breakdown</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Recent Insights */}
        <Card>
          <div className="flex items-center justify-between mb-6 p-6 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center">
              <Brain className="w-5 h-5 mr-2 text-sage-600" />
              AI Insights
            </h3>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => toast.success('Advanced insights coming in Phase 2')}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              View All
            </Button>
          </div>
          <div className="space-y-4 p-6">
            {insights.map((insight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`border-l-4 ${insight.color} ${insight.bgColor} p-4 rounded-r-lg`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className={`font-medium ${insight.textColor} mb-1`}>
                      {insight.title}
                    </h4>
                    <p className={`text-sm ${insight.textColor} opacity-90 mb-2`}>
                      {insight.message}
                    </p>
                    <p className="text-xs text-slate-500">
                      {insight.time}
                    </p>
                  </div>
                  <div className="ml-4">
                    {insight.type === 'positive' && <Smile className="w-5 h-5 text-emerald-500" />}
                    {insight.type === 'info' && <Activity className="w-5 h-5 text-blue-500" />}
                    {insight.type === 'neutral' && <BarChart3 className="w-5 h-5 text-slate-500" />}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </Card>

        {/* Phase 2 Preview */}
        <Card className="mt-8 bg-gradient-to-r from-sage-50 to-blue-50 border-sage-200">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gradient-to-r from-sage-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              Advanced Analytics Coming Soon
            </h3>
            <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
              Phase 2 will include detailed sentiment analysis, predictive insights, 
              custom report generation, and real-time dashboard updates.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-600 mb-6">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-sage-500 rounded-full mr-2"></div>
                Voice Sentiment Analysis
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                Predictive Insights
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></div>
                Custom Reports
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                Real-time Updates
              </div>
            </div>
            <Button 
              variant="outline" 
              className="border-sage-300 text-sage-700 hover:bg-sage-100"
              onClick={() => toast.success('Phase 2 features will be available soon!')}
            >
              Learn More About Phase 2
            </Button>
          </div>
        </Card>
      </div>

      {/* Detailed Report Modal */}
      {showReportModal && selectedEmployee && selectedAssessment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-900">
                  Assessment Report: {selectedEmployee.first_name} {selectedEmployee.last_name}
                </h3>
                <Button
                  variant="ghost"
                  onClick={() => setShowReportModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-6">
                {/* Employee Summary */}
                <Card className="p-6 bg-gradient-to-r from-sage-50 to-emerald-50 border-sage-200">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                      <span className="text-sage-600 font-medium text-lg">
                        {selectedEmployee.first_name[0]}{selectedEmployee.last_name[0]}
                      </span>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-slate-900">
                        {selectedEmployee.first_name} {selectedEmployee.last_name}
                      </h4>
                      <p className="text-slate-600">{selectedEmployee.email}</p>
                      <p className="text-slate-500 text-sm">
                        {selectedEmployee.department || 'No department'} • Assessment completed on {formatAssessmentDate(selectedAssessment.completed_at)}
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Assessment Summary */}
                <Card className="p-6">
                  <h4 className="font-semibold text-slate-900 mb-4">Assessment Summary</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-sage-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-sage-700">
                        {Math.round((selectedAssessment.overall_metrics?.sentiment_score || 0.5) * 100)}%
                      </div>
                      <div className="text-sm text-sage-600">Overall Sentiment</div>
                    </div>
                    
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-700">
                        {Math.round((selectedAssessment.overall_metrics?.engagement_level || 0.5) * 100)}%
                      </div>
                      <div className="text-sm text-blue-600">Engagement Level</div>
                    </div>
                    
                    <div className="bg-amber-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-amber-700">
                        {Math.round((selectedAssessment.overall_metrics?.stress_level || 0.5) * 100)}%
                      </div>
                      <div className="text-sm text-amber-600">Stress Level</div>
                    </div>
                  </div>
                  
                  {/* Assessment Type */}
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center mr-3">
                      {selectedAssessment.assessment_type === 'voice' || selectedAssessment.assessment_type === 'conversational_voice' ? (
                        <Mic className="w-4 h-4 text-slate-600" />
                      ) : selectedAssessment.assessment_type === 'text' ? (
                        <FileText className="w-4 h-4 text-slate-600" />
                      ) : (
                        <CheckSquare className="w-4 h-4 text-slate-600" />
                      )}
                    </div>
                    <div>
                      <h5 className="font-medium text-slate-900">
                        {getAssessmentTypeLabel(selectedAssessment.assessment_type)}
                      </h5>
                    </div>
                  </div>
                  
                  {/* Recommendations */}
                  {selectedAssessment.recommendations && selectedAssessment.recommendations.length > 0 && (
                    <div className="mb-4">
                      <h5 className="font-medium text-slate-900 mb-3">Recommendations:</h5>
                      <ul className="space-y-2">
                        {selectedAssessment.recommendations.map((rec: string, idx: number) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span className="text-slate-600">{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card>

                {/* Assessment Details */}
                <Card className="p-6">
                  <h4 className="font-semibold text-slate-900 mb-4">Assessment Details</h4>
                  
                  {/* Questions and Answers */}
                  {getAssessmentQuestionsAndAnswers(selectedAssessment)}
                </Card>

                {/* Actions */}
                <div className="flex justify-end space-x-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowReportModal(false)}
                    className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    Close
                  </Button>
                  
                  <Button
                    onClick={() => sendAssessmentReportEmail(selectedEmployee, selectedAssessment)}
                    className="bg-sage-600 hover:bg-sage-700 text-white"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email Report
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )

  // Helper function to extract questions and answers from assessment
  function getAssessmentQuestionsAndAnswers(assessment: any) {
    if (!assessment) return <p className="text-slate-500">No detailed responses available</p>
    
    let questionsAndAnswers = []
    
    if (assessment.assessment_type === 'text' && assessment.responses) {
      questionsAndAnswers = assessment.responses.map((qa: any) => ({
        question: qa.question,
        answer: qa.response
      }))
    } else if (assessment.assessment_type === 'mcq' && assessment.responses) {
      // For MCQ, we need to map the responses to the questions
      const mcqQuestions = [
        { id: 'stress_level', question: 'How would you rate your current stress level at work?' },
        { id: 'job_satisfaction', question: 'How satisfied are you with your current role and responsibilities?' },
        { id: 'workload_management', question: 'How manageable is your current workload?' },
        { id: 'team_support', question: 'How supported do you feel by your team and manager?' },
        { id: 'work_life_balance', question: 'How would you rate your work-life balance?' }
      ]
      
      questionsAndAnswers = Object.entries(assessment.responses).map(([id, value]) => {
        const question = mcqQuestions.find(q => q.id === id)?.question || id
        return {
          question,
          answer: `Rating: ${value}/5`
        }
      })
    } else if (assessment.assessment_type === 'voice' || assessment.assessment_type === 'conversational_voice') {
      // For voice, we extract the transcript
      if (assessment.conversation_data?.transcript) {
        questionsAndAnswers = [{
          question: 'Voice Transcript',
          answer: assessment.conversation_data.transcript
        }]
      } else if (assessment.conversation_data?.conversation_turns) {
        // For conversational voice, extract the conversation turns
        const turns = assessment.conversation_data.conversation_turns
        questionsAndAnswers = turns.filter((turn: any) => turn.speaker === 'user').map((turn: any, index: number) => ({
          question: `Response ${index + 1}`,
          answer: turn.text
        }))
      }
    }
    
    if (questionsAndAnswers.length === 0) {
      return <p className="text-slate-500">No detailed responses available for this assessment type</p>
    }
    
    return (
      <div className="space-y-4">
        {questionsAndAnswers.map((qa: any, index: number) => (
          <div key={index} className="bg-slate-50 rounded-lg p-4">
            <h5 className="font-medium text-slate-900 mb-2">{qa.question}</h5>
            <p className="text-slate-600 whitespace-pre-wrap">{qa.answer}</p>
          </div>
        ))}
      </div>
    )
  }

  // Helper function to generate detailed report
  function generateDetailedReport(employee: any, assessment: any) {
    const formatDate = (dateString: string) => {
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    }

    // Extract assessment data
    const assessmentType = getAssessmentTypeLabel(assessment.assessment_type)
    const completedAt = formatDate(assessment.completed_at)
    const sentimentScore = Math.round((assessment.overall_metrics?.sentiment_score || 0.5) * 100)
    const engagementLevel = Math.round((assessment.overall_metrics?.engagement_level || 0.5) * 100)
    const stressLevel = assessment.overall_metrics?.stress_level !== undefined 
      ? Math.round(assessment.overall_metrics.stress_level * 100) 
      : 'N/A'
    
    // Get questions and answers
    let questionsAndAnswers = []
    
    if (assessment.assessment_type === 'text' && assessment.responses) {
      questionsAndAnswers = assessment.responses.map((qa: any) => ({
        question: qa.question,
        answer: qa.response
      }))
    } else if (assessment.assessment_type === 'mcq' && assessment.responses) {
      // For MCQ, we need to map the responses to the questions
      const mcqQuestions = [
        { id: 'stress_level', question: 'How would you rate your current stress level at work?' },
        { id: 'job_satisfaction', question: 'How satisfied are you with your current role and responsibilities?' },
        { id: 'workload_management', question: 'How manageable is your current workload?' },
        { id: 'team_support', question: 'How supported do you feel by your team and manager?' },
        { id: 'work_life_balance', question: 'How would you rate your work-life balance?' }
      ]
      
      questionsAndAnswers = Object.entries(assessment.responses).map(([id, value]) => {
        const question = mcqQuestions.find(q => q.id === id)?.question || id
        return {
          question,
          answer: `Rating: ${value}/5`
        }
      })
    } else if (assessment.assessment_type === 'voice' || assessment.assessment_type === 'conversational_voice') {
      // For voice, we extract the transcript
      if (assessment.conversation_data?.transcript) {
        questionsAndAnswers = [{
          question: 'Voice Transcript',
          answer: assessment.conversation_data.transcript
        }]
      } else if (assessment.conversation_data?.conversation_turns) {
        // For conversational voice, extract the conversation turns
        const turns = assessment.conversation_data.conversation_turns
        questionsAndAnswers = turns.filter((turn: any) => turn.speaker === 'user').map((turn: any, index: number) => ({
          question: `Response ${index + 1}`,
          answer: turn.text
        }))
      }
    }

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Assessment Report - ${employee.first_name} ${employee.last_name}</title>
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
              max-width: 800px; 
              margin: 0 auto; 
              padding: 20px; 
              line-height: 1.6;
              color: #334155;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              padding: 20px;
              background: linear-gradient(135deg, #5d7360 0%, #10b981 100%);
              color: white;
              border-radius: 12px;
            }
            .section {
              margin-bottom: 30px;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 20px;
            }
            .section h2 {
              margin-top: 0;
              color: #1e293b;
              border-bottom: 1px solid #e2e8f0;
              padding-bottom: 10px;
            }
            .metrics {
              display: flex;
              flex-wrap: wrap;
              gap: 15px;
              margin-bottom: 20px;
            }
            .metric {
              flex: 1;
              min-width: 150px;
              background: #f8fafc;
              padding: 15px;
              border-radius: 8px;
              text-align: center;
            }
            .metric .value {
              font-size: 2em;
              font-weight: bold;
              color: #0f766e;
              margin: 10px 0;
            }
            .metric .label {
              color: #64748b;
              font-size: 0.9em;
            }
            .recommendations {
              background: #f0fdf4;
              padding: 15px;
              border-radius: 8px;
              margin-bottom: 20px;
            }
            .recommendations h3 {
              color: #166534;
              margin-top: 0;
            }
            .recommendations ul {
              margin: 0;
              padding-left: 20px;
            }
            .recommendations li {
              margin: 8px 0;
              color: #15803d;
            }
            .qa-item {
              background: white;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 15px;
              margin-bottom: 15px;
            }
            .qa-item .question {
              font-weight: bold;
              color: #1e293b;
              margin-bottom: 8px;
            }
            .qa-item .answer {
              color: #334155;
              white-space: pre-wrap;
            }
            .footer {
              text-align: center;
              margin-top: 40px;
              color: #64748b;
              font-size: 0.9em;
            }
            .confidential {
              background: #fff7ed;
              border: 1px solid #f59e0b;
              border-radius: 8px;
              padding: 15px;
              margin: 20px 0;
              text-align: center;
              color: #92400e;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Detailed Assessment Report</h1>
            <h2>${employee.first_name} ${employee.last_name}</h2>
            <p>${employee.department || 'No department'} • ${assessmentType}</p>
            <p>Completed on ${completedAt}</p>
          </div>
          
          <div class="section">
            <h2>Assessment Summary</h2>
            
            <div class="metrics">
              <div class="metric">
                <div class="label">Overall Sentiment</div>
                <div class="value">${sentimentScore}%</div>
              </div>
              
              <div class="metric">
                <div class="label">Engagement Level</div>
                <div class="value">${engagementLevel}%</div>
              </div>
              
              <div class="metric">
                <div class="label">Stress Level</div>
                <div class="value">${stressLevel}%</div>
              </div>
            </div>
            
            ${assessment.recommendations && assessment.recommendations.length > 0 ? `
              <div class="recommendations">
                <h3>Personalized Recommendations</h3>
                <ul>
                  ${assessment.recommendations.map((rec: string) => `<li>${rec}</li>`).join('')}
                </ul>
              </div>
            ` : ''}
            
            ${assessment.insights && assessment.insights.length > 0 ? `
              <div class="insights">
                <h3>Key Insights</h3>
                <ul>
                  ${assessment.insights.map((insight: any) => `
                    <li>
                      <strong>${insight.title || 'Insight'}</strong>: 
                      ${insight.description || insight.message || ''}
                    </li>
                  `).join('')}
                </ul>
              </div>
            ` : ''}
          </div>
          
          <div class="section">
            <h2>Assessment Details</h2>
            
            ${questionsAndAnswers.length > 0 ? `
              <div class="qa-section">
                ${questionsAndAnswers.map((qa: any, index: number) => `
                  <div class="qa-item">
                    <div class="question">Q${index + 1}: ${qa.question}</div>
                    <div class="answer">${qa.answer}</div>
                  </div>
                `).join('')}
              </div>
            ` : `
              <p>No detailed responses available for this assessment type.</p>
            `}
          </div>
          
          <div class="confidential">
            <strong>Confidential Information</strong><br>
            This report contains sensitive employee wellness data and should be handled according to your company's privacy policies and HIPAA compliance requirements.
          </div>
          
          <div class="footer">
            <p>Generated by Mentra on ${new Date().toLocaleString()}</p>
            <p>© 2025 Mentra. All rights reserved.</p>
          </div>
        </body>
      </html>
    `
  }
}